'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { TrackframeHeaderLogo, TrackframeHeaderRoot, } from './primitives.js';
import { hubUrl } from '../../../hub-url.js';
import { TrackframeHeaderMenu } from './header-menu.js';
import { useTrackframeHeaderMenu } from './use-header-menu.js';
/**
 * Shared application header. Logo on the left, optional `children` in the
 * center, ONE dropdown menu on the right (HUB-125): avatar → identity,
 * navigable tracks, Portal / Admin, the user's own pages, sign-out.
 *
 * Pulls identity from `/auth/me`, branding from `/settings/branding`, and
 * navigable tracks from `/tracks`. Logo bytes load from `/settings/logo`
 * (cross-origin `<img>` works without preflight; the auth hook bypasses that
 * route so unauthenticated `<img>` requests succeed for branding).
 *
 * Two deployment modes:
 *
 *   - Same-origin (hub admin SPA, hub root):
 *       <TrackframeHeader hubBaseUrl={hubBaseUrl} />
 *
 *   - Track frontend (different origin from the hub):
 *       <TrackframeHeader
 *         hubBaseUrl="https://hub.example.com"
 *         hubProxyBaseUrl="/hub"
 *         getAuthHeaders={() => ({ Authorization: `Bearer ${jwt}` })}
 *       >
 *         {breadcrumbs}
 *       </TrackframeHeader>
 */
export function TrackframeHeader({ hubBaseUrl, hubProxyBaseUrl, getAuthHeaders, onLogoClick, signOut: signOutOpts, children, brandFallback, menuItems, className, }) {
    // One path for the menu: the same hook a track with its own dropdown uses
    // (identity + branding + tracks → items, and the sign-out action).
    const { identity, branding, items, signOut: handleSignOut } = useTrackframeHeaderMenu({
        hubBaseUrl,
        hubProxyBaseUrl,
        getAuthHeaders,
        signOut: signOutOpts,
    });
    // Logo bytes live on the hub; prefix the hub-relative URLs with hubBaseUrl
    // so a track on another origin loads them cross-origin.
    const onHub = (path) => (path ? hubUrl(hubBaseUrl, path) : null);
    const logoSrc = onHub(branding?.logoUrl);
    const logoDarkSrc = onHub(branding?.logoDarkUrl);
    const logoNode = (_jsx(TrackframeHeaderLogo, { src: logoSrc, darkSrc: logoDarkSrc, fallback: brandFallback, alt: "Trackframe" }));
    return (_jsxs(TrackframeHeaderRoot, { className: className, children: [onLogoClick ? (_jsx("button", { type: "button", className: "tf-header-logo-button", onClick: onLogoClick, "aria-label": "Home", children: logoNode })) : (logoNode), _jsx("div", { className: "tf-header-center", children: children }), _jsx("div", { className: "tf-header-actions", children: _jsx(TrackframeHeaderMenu, { email: identity?.email ?? null, name: identity?.name ?? null, avatarUrl: identity?.avatarUrl ?? null, items: items, onSignOut: handleSignOut, children: menuItems }) })] }));
}
//# sourceMappingURL=header.js.map